import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import {
  Loader2, Crown, Trophy, Heart, MessageCircle, Eye, UserPlus,
  Gift, ShoppingBag, Users, Sparkles,
} from "lucide-react";

const PODIUM = ["#FF2D7A", "#FFB42D", "#00E0C7"]; // gradient stops as rank colours
const GIFT_PODIUM_STYLE = [
  { gradient: "linear-gradient(135deg,#FFD700,#FFB42D,#FF6B00)", icon: Crown,  border: "#FFD700", label: "1st" },
  { gradient: "linear-gradient(135deg,#C0C0C0,#9BA1A8,#737B7E)", icon: Trophy, border: "#C0C0C0", label: "2nd" },
  { gradient: "linear-gradient(135deg,#CD7F32,#A05A21,#7C4115)", icon: Trophy, border: "#CD7F32", label: "3rd" },
];
const WINDOW_OPTIONS = [
  { value: 7,   label: "This week" },
  { value: 30,  label: "30 days" },
  { value: 90,  label: "Quarter" },
  { value: 365, label: "All-time" },
];

const fmt = (n) => {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const PlyxLeaderboard = () => {
  // Two boards on the same surface — engagement OR gift revenue. URL-hash
  // controlled so creators can share `…/leaderboard#gifts` direct.
  const initialTab = (typeof window !== "undefined" && window.location.hash === "#gifts") ? "gifts" : "engagement";
  const [tab, setTab] = useState(initialTab);
  const [giftWindow, setGiftWindow] = useState(30);
  const [engagement, setEngagement] = useState(null);
  const [gifts, setGifts] = useState(null);
  const [loadingE, setLoadingE] = useState(false);
  const [loadingG, setLoadingG] = useState(false);

  useEffect(() => {
    if (tab !== "engagement" || engagement) return;
    setLoadingE(true);
    plyxApi.get("/plyx/leaderboard")
      .then((r) => setEngagement(r.data))
      .finally(() => setLoadingE(false));
  }, [tab, engagement]);

  useEffect(() => {
    if (tab !== "gifts") return;
    setLoadingG(true);
    plyxApi.get(`/plyx/leaderboard/gift-power?window=${giftWindow}&limit=50`)
      .then((r) => setGifts(r.data))
      .finally(() => setLoadingG(false));
  }, [tab, giftWindow]);

  const switchTab = (next) => {
    setTab(next);
    try { window.history.replaceState(null, "", next === "gifts" ? "#gifts" : "#engagement"); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-leaderboard-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle at 30% 0%, #FFB42D 0%, transparent 50%), radial-gradient(circle at 70% 100%, #FF2D7A 0%, transparent 50%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 py-10 sm:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.3em] font-semibold text-[#FFB42D] mb-4">
            <Trophy className="w-3 h-3"/> Plyx Leaderboard
          </div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight mb-3">
            Where momentum becomes <span className="bg-clip-text text-transparent" style={{ backgroundImage: "#E85D4E" }}>movement</span>.
          </h1>

          {/* Tab switcher */}
          <div className="mt-7 inline-flex bg-black/40 border border-white/10 rounded-full p-1" data-testid="plyx-leaderboard-tabs">
            <button
              type="button"
              onClick={() => switchTab("engagement")}
              data-testid="plyx-leaderboard-tab-engagement"
              className={`px-5 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 transition-colors ${
                tab === "engagement" ? "bg-white text-[#0A0A0A]" : "text-white/70 hover:text-white"
              }`}
            >
              <Sparkles className="w-3 h-3"/> Most engaging
            </button>
            <button
              type="button"
              onClick={() => switchTab("gifts")}
              data-testid="plyx-leaderboard-tab-gifts"
              className={`px-5 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 transition-colors ${
                tab === "gifts" ? "bg-white text-[#0A0A0A]" : "text-white/70 hover:text-white"
              }`}
            >
              <Gift className="w-3 h-3"/> Gift power
            </button>
          </div>

          {/* Window switcher (gifts only) */}
          {tab === "gifts" && (
            <div className="mt-4 inline-flex bg-black/40 border border-white/10 rounded-full p-1" data-testid="plyx-leaderboard-window-switcher">
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setGiftWindow(opt.value)}
                  data-testid={`plyx-leaderboard-window-${opt.value}`}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                    giftWindow === opt.value ? "bg-white/20 text-white" : "text-white/60 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-10 pb-24">
        {tab === "engagement"
          ? <EngagementBoard data={engagement} loading={loadingE}/>
          : <GiftsBoard data={gifts} loading={loadingG}/>}
      </div>
    </div>
  );
};

// ── Engagement board (original, score-based) ───────────────────────────────
const EngagementBoard = ({ data, loading }) => {
  if (loading || !data) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>;
  }
  return (
    <>
      <p className="text-sm text-white/60 max-w-xl mx-auto text-center -mt-2 mb-8">
        The {data.prize_top_n || 10} most-engaging creators on Plyx this month. Rank reflects views, likes, comments, shares, and new followers in the last {data.window_days} days.
      </p>
      {(data.leaders || []).length === 0 ? (
        <div className="p-10 rounded-2xl bg-white/5 border border-white/10 text-center text-white/60">
          <Trophy className="w-8 h-8 mx-auto mb-3 text-[#FFB42D]"/>
          No leaderboard yet — post a video to be the first.
          <div className="mt-5">
            <Link
              to="/plyx/upload"
              data-testid="plyx-leaderboard-upload-cta"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-white text-sm"
              style={{ background: "#E85D4E" }}
            >
              Upload a video
            </Link>
          </div>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.leaders.map((l) => {
            const podium = l.position <= 3 ? PODIUM[l.position - 1] : null;
            return (
              <li
                key={l.plyx_user_id}
                data-testid={`plyx-leader-row-${l.position}`}
                className={`p-4 rounded-2xl border flex items-center gap-4 ${
                  l.in_prize_zone ? "bg-white/8 border-white/20" : "bg-white/4 border-white/10"
                }`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-display text-base text-white shrink-0"
                  style={{ background: podium || "rgba(255,255,255,0.15)", color: podium ? "#0A0A0A" : "#fff" }}
                >
                  {l.position <= 3 ? <Crown className="w-5 h-5"/> : `#${l.position}`}
                </div>
                <Link to={`/plyx/u/${l.handle}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80">
                  <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden flex items-center justify-center shrink-0">
                    {l.avatar_url
                      ? <img src={plyxMediaUrl(l.avatar_url)} alt="" className="w-full h-full object-cover"/>
                      : (l.display_name?.slice(0, 1) || "?")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">@{l.handle}</div>
                    <div className="text-xs text-white/50 truncate">
                      {l.display_name}{l.country ? ` · ${l.country}` : ""}
                    </div>
                  </div>
                </Link>
                <div className="hidden sm:flex items-center gap-4 text-xs text-white/70">
                  <span className="inline-flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5"/> {fmt(l.views)}</span>
                  <span className="inline-flex items-center gap-1" title="Likes"><Heart className="w-3.5 h-3.5"/> {fmt(l.likes)}</span>
                  <span className="inline-flex items-center gap-1" title="Comments"><MessageCircle className="w-3.5 h-3.5"/> {fmt(l.comments)}</span>
                  {l.new_followers_30d > 0 && (
                    <span className="inline-flex items-center gap-1 text-white/85" title="New followers (30d)"><UserPlus className="w-3.5 h-3.5"/> +{fmt(l.new_followers_30d)}</span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl">{l.score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Score</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-8 text-xs text-white/40 max-w-md mx-auto text-center">
        Score formula: views×0.5 + likes×3 + comments×5 + shares×4 + new followers (30d)×8.
      </div>
    </>
  );
};

// ── Gift Power board ───────────────────────────────────────────────────────
const GiftsBoard = ({ data, loading }) => {
  if (loading || !data) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>;
  }
  const board = data.leaderboard || [];
  const podium = board.slice(0, 3);
  const rest = board.slice(3);

  if (board.length === 0) {
    return (
      <div className="p-10 rounded-2xl bg-white/5 border border-white/10 text-center text-white/60" data-testid="plyx-leaderboard-gifts-empty">
        <Gift className="w-10 h-10 mx-auto mb-3 text-[#FFB42D]"/>
        No gifts in this window — find a creator you love and send them one.
        <div className="mt-5">
          <Link to="/plyx/feed" className="inline-block px-5 py-2 rounded-full bg-white text-white text-xs font-bold">
            Browse feed
          </Link>
        </div>
      </div>
    );
  }
  return (
    <>
      <p className="text-sm text-white/60 max-w-xl mx-auto text-center -mt-2 mb-8">
        Top creators by gift revenue in the last {data.window_days} days. Tap any creator to visit their shop or profile.
      </p>

      {/* Podium */}
      {podium.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="plyx-leaderboard-gifts-podium">
          {podium.map((c) => (<PodiumCard key={c.plyx_user_id} creator={c}/>))}
        </div>
      )}

      {/* Rest 4–50 */}
      {rest.length > 0 && (
        <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/5" data-testid="plyx-leaderboard-gifts-list">
          {rest.map((c, i) => (<GiftRow key={c.plyx_user_id} creator={c} striped={i % 2 === 1}/>))}
        </div>
      )}

      <div className="text-center text-[11px] text-white/40 mt-6">
        Showing {board.length} creators · updated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "just now"}
      </div>
    </>
  );
};

const PodiumCard = ({ creator }) => {
  const style = GIFT_PODIUM_STYLE[creator.rank - 1] || GIFT_PODIUM_STYLE[2];
  const Icon = style.icon;
  return (
    <Link
      to={creator.profile_url || "#"}
      data-testid={`plyx-leaderboard-gifts-podium-${creator.rank}`}
      className="relative rounded-3xl p-6 overflow-hidden flex flex-col items-center text-center transition-transform hover:scale-[1.02]"
      style={{
        background: `${style.gradient}, #0A0A0A`,
        backgroundBlendMode: "soft-light",
        border: `2px solid ${style.border}55`,
        boxShadow: `0 0 32px ${style.border}33`,
      }}
    >
      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1">
        <Icon className="w-3 h-3" style={{ color: style.border }}/> {style.label}
      </div>

      <div className="w-20 h-20 rounded-full overflow-hidden bg-black/40 border-2 mb-3" style={{ borderColor: style.border }}>
        {creator.avatar_url ? (
          <img src={plyxMediaUrl(creator.avatar_url)} alt={creator.display_name} className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl font-display">
            {(creator.display_name || creator.handle || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>

      <div className="font-display text-lg leading-tight">{creator.display_name}</div>
      <div className="text-xs text-white/70 mb-3">@{creator.handle}</div>

      <div className="font-display text-3xl tabular-nums" style={{ color: style.border }}>
        £{creator.earned_gbp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-white/60 mt-0.5">
        earned · {creator.gifts_received_count} gift{creator.gifts_received_count === 1 ? "" : "s"} from {creator.unique_gifters} fan{creator.unique_gifters === 1 ? "" : "s"}
      </div>

      {creator.top_emojis?.length > 0 && (
        <div className="flex gap-1.5 mt-2 text-xl">
          {creator.top_emojis.slice(0, 4).map((e, i) => (<span key={i}>{e}</span>))}
        </div>
      )}

      {creator.shop_product_count > 0 && creator.shop_url && (
        <Link
          to={creator.shop_url}
          onClick={(e) => e.stopPropagation()}
          data-testid={`plyx-leaderboard-gifts-podium-shop-${creator.rank}`}
          className="mt-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-xs font-semibold border border-white/15 hover:bg-black inline-flex items-center gap-1.5"
        >
          <ShoppingBag className="w-3 h-3"/> Visit shop · {creator.shop_product_count}
        </Link>
      )}
    </Link>
  );
};

const GiftRow = ({ creator, striped }) => (
  <Link
    to={creator.profile_url || "#"}
    data-testid={`plyx-leaderboard-gifts-row-${creator.rank}`}
    className={`flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition-colors ${striped ? "bg-white/[0.02]" : ""}`}
  >
    <div className="font-display text-xl tabular-nums w-10 text-center text-white/40">{creator.rank}</div>
    <div className="w-11 h-11 rounded-full bg-black/40 overflow-hidden shrink-0">
      {creator.avatar_url ? (
        <img src={plyxMediaUrl(creator.avatar_url)} alt={creator.display_name} className="w-full h-full object-cover"/>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-lg font-display bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]">
          {(creator.display_name || creator.handle || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm truncate">{creator.display_name}</div>
      <div className="text-[11px] text-white/50 inline-flex items-center gap-2 flex-wrap">
        <span>@{creator.handle}</span>
        {creator.shop_product_count > 0 && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5 text-[#FFB42D]">
              <ShoppingBag className="w-2.5 h-2.5"/> {creator.shop_product_count}
            </span>
          </>
        )}
        <span>·</span>
        <span className="inline-flex items-center gap-0.5">
          <Users className="w-2.5 h-2.5"/> {creator.unique_gifters}
        </span>
      </div>
    </div>
    <div className="text-right shrink-0">
      <div className="font-display text-lg text-[#FFB42D] tabular-nums">
        £{creator.earned_gbp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      </div>
      <div className="text-[10px] text-white/40 uppercase tracking-wider">
        {creator.gifts_received_count} gift{creator.gifts_received_count === 1 ? "" : "s"}
      </div>
    </div>
  </Link>
);

export default PlyxLeaderboard;
